package dev.lingvo.site.pages.app.projects.slug

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import dev.lingvo.shared.AppStoreKeyInput
import dev.lingvo.shared.CreateStoreConnectionRequest
import dev.lingvo.shared.GooglePlayKeyInput
import dev.lingvo.shared.StorePlatform
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LTextArea
import dev.lingvo.site.ds.LTextField
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLElement

@Composable
internal fun StoreCredentialStep(platform: StorePlatform, busy: Boolean, onConnect: (CreateStoreConnectionRequest) -> Unit) {
    var label by remember { mutableStateOf(platform.displayName) }
    var kind by remember { mutableStateOf("individual") }
    var issuer by remember { mutableStateOf("") }
    var keyId by remember { mutableStateOf("") }
    var secret by remember { mutableStateOf("") }
    var advanced by remember { mutableStateOf(false) }
    var submitted by remember { mutableStateOf(false) }
    val apple = platform == StorePlatform.APP_STORE
    val required = t("app.projects.slug.store_connection_credentials.fill_in_this_field", "Fill in this field.")
    Column(Modifier.fillMaxWidth().gap(Space.MD).attr("data-store-credential", platform.id)) {
        LTextField(label, { label = it }, modifier = Modifier.fillMaxWidth(), label = t("app.projects.slug.store_connection_credentials.connection_name", "Connection name"),
            error = if (submitted && label.isBlank()) required else null, enabled = !busy)
        if (apple) {
            LSelect(kind, listOf(
                LSelectOption("individual", t("app.projects.slug.store_connection_credentials.individual_key_recommended", "Individual key (recommended)")),
                LSelectOption("team", t("app.projects.slug.store_connection_credentials.team_key", "Team key")),
            ), { kind = it; issuer = "" }, modifier = Modifier.fillMaxWidth().styleModifier { property("min-width", "0"); property("max-width", "100%") }, label = t("app.projects.slug.store_connection_credentials.key_type", "Key type"), enabled = !busy)
            LMuted(if (kind == "individual") t("app.projects.slug.store_connection_credentials.use_your_profiles_individual_api_key", "Use your profile's Individual API Key. It follows your role and app access; no issuer ID is needed.")
                else t("app.projects.slug.store_connection_credentials.create_a_team_key_in_users", "Create a team key in Users and Access → Integrations. Team keys can reach every app on the team; Lingvo will allow only the apps you choose."))
            LMuted(t("app.projects.slug.store_connection_credentials.listing_text_needs_marketing_or_higher", "Listing text needs Marketing or higher. Screenshots through the asset library need App Manager or higher."))
            if (kind == "team") LTextField(issuer, { issuer = it }, modifier = Modifier.fillMaxWidth(), label = t("app.projects.slug.store_connection_credentials.issuer_id", "Issuer ID"),
                error = if (submitted && issuer.isBlank()) required else null, enabled = !busy)
            LTextField(keyId, { keyId = it }, modifier = Modifier.fillMaxWidth(), label = t("app.projects.slug.store_connection_credentials.key_id", "Key ID"),
                error = if (submitted && keyId.isBlank()) required else null, enabled = !busy)
        } else {
            LMuted(t("app.projects.slug.store_connection_credentials.create_a_workspace_email_invite_it", "Create a workspace email, invite it in Play Console, then check your package name. No JSON key is needed."))
            LButton(
                if (advanced) t("app.projects.slug.store_connection_credentials.use_the_invite_email_instead", "Use the invite email instead") else t("app.projects.slug.store_connection_credentials.advanced_use_a_json_key", "Advanced: use a JSON key"),
                onClick = { advanced = !advanced; secret = ""; submitted = false }, variant = LButtonVariant.GHOST, enabled = !busy,
            )
        }
        if (apple || advanced) {
            LTextArea(secret, { secret = it }, modifier = Modifier.fillMaxWidth(),
                label = if (apple) t("app.projects.slug.store_connection_credentials.private_key_p8_file_text", "Private key (.p8 file text)") else t("app.projects.slug.store_connection_credentials.service_account_json_key", "Service account JSON key"),
                error = if (submitted && secret.isBlank()) required else null, enabled = !busy)
            LMuted(t("app.projects.slug.store_connection_credentials.encrypted_on_the_server_never_returned", "Encrypted on the server, never returned. The input is cleared when you submit."))
        }
        LButton(
            if (apple) t("app.projects.slug.store_connection_credentials.verify_key", "Verify key")
            else if (advanced) t("app.projects.slug.store_connection_credentials.save_advanced_key", "Save advanced key")
            else t("app.projects.slug.store_connection_credentials.create_invite_email", "Create invite email"),
            onClick = {
                submitted = true
                if (label.isBlank() || (apple && (keyId.isBlank() || (kind == "team" && issuer.isBlank()))) || ((apple || advanced) && secret.isBlank())) {
                    window.setTimeout({ (document.querySelector("[data-store-credential='${platform.id}'] [aria-invalid='true']") as? HTMLElement)?.focus() }, 0)
                } else {
                    val request = CreateStoreConnectionRequest(platform, label.trim(),
                        appStore = if (apple) AppStoreKeyInput(if (kind == "team") issuer.trim() else "", keyId.trim(), secret) else null,
                        googlePlay = if (!apple && advanced) GooglePlayKeyInput(secret) else null)
                    secret = ""
                    submitted = false
                    onConnect(request)
                }
            }, enabled = !busy, loading = busy,
        )
    }
}
